import React, { useCallback } from "react";
import { Box, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";

const SELECTED_BG = "teal.50";
const SELECTED_STRONG = "teal.100";
const SELECTED_FG = "teal.700";

/**
 * One cell as text.
 *
 * A cell can legitimately hold an object or an array — `rows_to_jsonable` passes a JSON-safe container
 * through unchanged so the destination gets a real map rather than Python's repr of one, and Foundation
 * Data v2's `metadata` column is exactly that. React cannot render an object as a child ("Objects are not
 * valid as a React child"), and it does not fail quietly: it takes down the whole grid.
 *
 * Stringified as JSON rather than shown as "[object Object]" — the point of a preview is to see what is
 * there, and a reader who cannot see a cell's contents cannot tell the agent what to do about it.
 */
export function formatCell(cell) {
  if (cell === null || cell === undefined) return "";
  if (typeof cell === "object") {
    try {
      return JSON.stringify(cell);
    } catch {
      // Circular or otherwise unserialisable. Cannot happen from JSON on the wire, but a cell is
      // arbitrary data and a grid that throws is worse than a cell that reads "[unrenderable]".
      return "[unrenderable]";
    }
  }
  if (typeof cell === "boolean") return String(cell);
  return cell;
}

export function DataPreviewTable({
  columns,
  rows,
  selectedColumns,
  selectedRows,
  selectedCells,
  onColumnClick,
  onRowClick,
  onCellClick,
}) {
  const isColSelected = useCallback(
    (i) => selectedColumns?.includes(i) ?? false,
    [selectedColumns]
  );
  const isRowSelected = useCallback(
    (i) => selectedRows?.includes(i) ?? false,
    [selectedRows]
  );
  const isCellSelected = useCallback(
    (r, c) => selectedCells?.some((cell) => cell.row === r && cell.col === c) ?? false,
    [selectedCells]
  );

  const hasSelection = Boolean(onColumnClick || onRowClick || onCellClick);

  const handleHeaderClick = (index, e) => {
    e.preventDefault();
    onColumnClick?.(index, e.shiftKey, e.ctrlKey || e.metaKey);
  };

  const handleRowClick = (index, e) => {
    e.preventDefault();
    onRowClick?.(index, e.shiftKey, e.ctrlKey || e.metaKey);
  };

  const handleCellClick = (row, col, e) => {
    e.preventDefault();
    onCellClick?.(row, col, false, e.ctrlKey || e.metaKey);
  };

  return (
    <Box flex="1" minH={0} minW={0} display="flex" flexDirection="column">
      <Box
        flex="1"
        overflow="auto"
        borderWidth="1px"
        borderRadius="md"
        sx={{
          "&::-webkit-scrollbar": { width: "6px", height: "6px" },
          "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
          "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
          scrollbarWidth: "thin",
          scrollbarColor: "transparent transparent",
          "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
        }}
      >
        <Table size="sm" variant="simple">
          <Thead position="sticky" top={0} bg="white" zIndex={1}>
            <Tr>
              {hasSelection && (
                <Th w="40px" textAlign="center" color="gray.500">
                  #
                </Th>
              )}
              {columns.map((col, i) => (
                <Th
                  key={col}
                  onClick={(e) => handleHeaderClick(i, e)}
                  cursor={onColumnClick ? "pointer" : "default"}
                  userSelect="none"
                  bg={isColSelected(i) ? SELECTED_STRONG : "gray.50"}
                  color={isColSelected(i) ? SELECTED_FG : undefined}
                  _hover={onColumnClick ? { bg: SELECTED_BG } : undefined}
                >
                  {col}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, i) => (
              <Tr key={i} bg={isRowSelected(i) ? SELECTED_BG : undefined}>
                {hasSelection && (
                  <Td
                    onClick={(e) => handleRowClick(i, e)}
                    cursor={onRowClick ? "pointer" : "default"}
                    userSelect="none"
                    color="gray.500"
                    fontFamily="mono"
                    fontSize="xs"
                    textAlign="center"
                    bg={isRowSelected(i) ? SELECTED_STRONG : undefined}
                    _hover={onRowClick ? { bg: SELECTED_BG } : undefined}
                  >
                    {i + 1}
                  </Td>
                )}
                {row.map((cell, j) => (
                  <Td
                    // eslint-disable-next-line react/no-array-index-key
                    key={j}
                    onClick={(e) => handleCellClick(i, j, e)}
                    cursor={onCellClick ? "pointer" : "default"}
                    bg={
                      isCellSelected(i, j)
                        ? SELECTED_STRONG
                        : isColSelected(j)
                        ? SELECTED_BG
                        : undefined
                    }
                    whiteSpace="nowrap"
                    fontSize="xs"
                  >
                    {formatCell(cell)}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      <Text fontSize="xs" color="gray.500" mt={2}>
        Showing {rows.length} rows
      </Text>
    </Box>
  );
}
